/**
 * T2-6561: helper for building the skill-id payload sent to
 * `POST /v3/skills/hygienist` (replace-all) when saving Qualifications & Skills.
 *
 * Qualification-level skills (e.g. CDA I vs CDA II, RDA, or the "trained on the
 * job" DA option) are mutually exclusive. The caller's checked-skill list is
 * seeded from the talent's existing skills, which can already contain BOTH levels
 * (the original bug: switching level appended the new one without removing the
 * old, so the replace-all persisted both and the switch appeared not to save).
 *
 * This helper strips every qualification-level id first, then appends exactly the
 * selected one (kept last so the backend `Hygienist.level` derivation, which reads
 * the last skill id, resolves correctly). Additional skills and the license skill
 * are preserved.
 */

export interface QualificationSkillLike {
  id: number;
  name?: string;
  label?: string;
  license?: boolean | number;
  qualification?: boolean | number;
}

export interface BuildHygienistSkillPayloadArgs {
  /** Ids of all currently-checked skills (may include stale qualification-level ids). */
  checkedSkillIds: number[];
  /** Selectable qualification-level skills (qualification && !license) — the dropdown options. */
  qualificationSkills: QualificationSkillLike[];
  /** The license-only skill (license && !qualification), e.g. RDH / QTDA. */
  licenseSkill?: QualificationSkillLike;
  /** The combined license+qualification skill (license && qualification), e.g. DA "trained on the job". */
  licenseQualificationSkill?: QualificationSkillLike;
  /** The qualification level the user selected in the dropdown (if any). */
  selectedQualificationSkill?: QualificationSkillLike;
  /** Whether the talent confirmed they are licensed/certified. */
  licensed?: boolean;
}

export const buildHygienistSkillPayload = ({
  checkedSkillIds,
  qualificationSkills,
  licenseSkill,
  licenseQualificationSkill,
  selectedQualificationSkill,
  licensed,
}: BuildHygienistSkillPayloadArgs): number[] => {
  const qualificationLevelIds = new Set<number>([
    ...qualificationSkills.map((skill) => skill.id),
    ...(licenseQualificationSkill ? [licenseQualificationSkill.id] : []),
  ]);

  const resultSkills: number[] = checkedSkillIds.filter(
    (id) => !qualificationLevelIds.has(id)
  );

  if (licensed && licenseSkill) {
    resultSkills.push(licenseSkill.id);
  }
  if (!licensed && licenseQualificationSkill) {
    resultSkills.push(licenseQualificationSkill.id);
  }
  // Keep the selected qualification level last so the backend's
  // Hygienist.level derivation (last skill id) resolves correctly.
  if (selectedQualificationSkill && licensed) {
    resultSkills.push(selectedQualificationSkill.id);
  }

  return Array.from(new Set(resultSkills));
};
